import type {
  KambiBetOffer,
  KambiEvent,
  KambiPrePack,
  KambiPrePackCoupon,
} from '@mobi/api-types/kambi'
import { simpleFetchWithLogging } from '../simpleFetchWithLogging'

export const getKambiBetOffersByIds = ({ betOfferIds }: { betOfferIds: number[] }) => {
  if (betOfferIds.length === 0) {
    return Promise.resolve({
      betOffers: [],
      events: [],
      prePacks: [],
      range: { start: 0, size: 0, total: 0, hasNext: false },
    })
  }
  return simpleFetchWithLogging<KambiBetOffersByIdsResponse>(
    `${window._kc.apiOfferingBaseUrl}betoffer/${betOfferIds.join(',')}?lang=en_AU&market=AU`
  )
}

export const getKambiBetOffersByEventIds = ({
  eventIds,
}: {
  eventIds: (number | string)[]
}): Promise<KambiBetOffersByIdsResponse> => {
  if (eventIds.length === 0) {
    return Promise.resolve({
      betOffers: [],
      events: [],
      prePacks: [],
      range: { start: 0, size: 0, total: 0, hasNext: false },
    })
  }
  return simpleFetchWithLogging<KambiBetOffersByIdsResponse>(
    `${window._kc.apiOfferingBaseUrl}betoffer/event/${eventIds.join(',')}?lang=en_AU&market=AU&includeParticipants=true`
  )
}

export const getKambiBetOffersByGroupIds = ({
  groupIds,
}: {
  groupIds: (number | string)[]
}): Promise<KambiBetOffersByIdsResponse> => {
  if (groupIds.length === 0) {
    return Promise.resolve({
      betOffers: [],
      events: [],
      prePacks: [],
      range: { start: 0, size: 0, total: 0, hasNext: false },
    })
  }
  const fetchPage = async (start: number): Promise<KambiBetOffersByIdsResponse> => {
    const page = await simpleFetchWithLogging<KambiBetOffersByIdsResponse>(
      `${window._kc.apiOfferingBaseUrl}betoffer/group/${groupIds.join(',')}?lang=en_AU&market=AU&includeParticipants=true&range_start=${start}`
    )
    const currentTotalPages = (page.range?.start ?? 0) + (page.range?.size ?? 0)
    if (page.range && currentTotalPages < page.range.total) {
      const nextStart = currentTotalPages + 1
      const next = await fetchPage(nextStart)
      return {
        ...next,
        betOffers: [...page.betOffers, ...next.betOffers],
        events: [...page.events, ...next.events],
        prePacks: [...page.prePacks, ...next.prePacks],
      }
    }

    return page
  }

  return fetchPage(0)
}

export const getKambiBetOffersByOutcomeIds = ({
  outcomeIds,
}: {
  outcomeIds: (number | string)[]
}): Promise<KambiBetOffersByIdsResponse> => {
  if (outcomeIds.length === 0) {
    return Promise.resolve({
      betOffers: [],
      events: [],
      prePacks: [],
      range: { start: 0, size: 0, total: 0, hasNext: false },
    })
  }
  return simpleFetchWithLogging<KambiBetOffersByIdsResponse>(
    `${window._kc.apiOfferingBaseUrl}betoffer/outcome?id=${outcomeIds.join('&id=')}&lang=en_AU&market=AU&includeParticipants=false&range_size=1000`
  )
}

export const getKambiBetOffersByPrePackSelectionIds = ({
  prePackSelectionIds,
}: {
  prePackSelectionIds: (number | string)[]
}): Promise<KambiPrePackCouponResponse> => {
  if (prePackSelectionIds.length === 0) {
    return Promise.resolve({
      betOffers: [],
      events: [],
      prePackCoupons: [],
      availablePrePackCouponTags: [],
    })
  }
  return simpleFetchWithLogging<KambiPrePackCouponResponse>(
    `${window._kc.apiOfferingBaseUrl}prepackcoupon/${prePackSelectionIds.join(',')}?lang=en_AU&market=AU&includeParticipants=false&range_size=1000`
  )
}

// =====
// Types
// =====

export type KambiBetOffersByIdsResponse = {
  betOffers: KambiBetOffer[]
  events: KambiEvent[]
  prePacks: KambiPrePack[]
  range?: {
    start: number
    size: number
    total: number
    hasNext: boolean
  }
}

export type KambiPrePackCouponResponse = {
  betOffers: KambiBetOffer[]
  events: KambiEvent[]
  prePackCoupons: KambiPrePackCoupon[]
  availablePrePackCouponTags?: string[]
}
